import { isMatch, reduce, some } from 'lodash';
import React, { useMemo } from 'react';
import {
  ConsumerExploreDishFirstQuery,
  DishFirstFoodItemsSegmentFragment,
  DishFirstSegmentFilter,
  ExploreFoodItemsSegmentLayout,
} from 'src/gqlReactTypings.generated.d';
import { DishFirstCardCollection } from 'src/pages/consumer/dish-first/explore/components/Segments/DishFirstCardCollection/DishFirstCardCollection';
import { DishFirstCardRow } from 'src/pages/consumer/dish-first/explore/components/Segments/DishFirstCardRow';
import { NoResults } from 'src/pages/consumer/dish-first/explore/components/Segments/NoResults';
import { ISegmentMetrics } from 'src/pages/consumer/explore/SegmentAnalytics';
import { UseExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { WavyEdge } from 'src/shared/design-system/WavyEdge';
import { useSelector } from 'src/store';
import { selectLineItemAvailabilitiesById } from 'src/store/cart/selectors';
import { match, P } from 'ts-pattern';
import { consolidateContentIfNecessary } from '../convertSegments';
import { isFoodItem } from '../DishFirstCardCollection/utils';
import { DishFirstShefsCardCollection } from '../DishFirstShefsCardCollection';
import { DishFirstShefsCardRow } from '../DishFirstShefsCardRow';
import { foodItemsHasMultipleMealTypes } from '../utils';
import { DividerContainer, SegmentMargins } from './styles';
import { DishFirstExploreSegmentProps, PageSegmentsProps } from './types';
import { getSegmentContentType, getSegmentInfo } from './utils';

/**
 * Right now, this is specifically just for specialty segments that have a shef segment
 * at the top and a dish segment at the bottom where the dish segment only contains one meal type.
 *
 * In such cases, there is no natural divider between the two segments, so we have to insert one.
 */
const insertDividersIfNecessary = (
  filters: UseExploreUrl<DishFirstSegmentFilter>,
  content: ConsumerExploreDishFirstQuery['exploreFoodItems']
) => {
  if (content.length !== 2) {
    return content;
  }
  const firstSegmentIsShefCarousel = isMatch(getSegmentInfo(filters, content[0]), {
    isSpecialtySegment: true,
    contentType: 'ExploreSegmentShef',
    layout: ExploreFoodItemsSegmentLayout.Carousel,
  });
  const secondSegmentIsDishCollection = isMatch(getSegmentInfo(filters, content[1]), { isSpecialtySegment: true });
  const hasMultipleMealTypes = foodItemsHasMultipleMealTypes(content[1].content.items.filter(isFoodItem));
  if (firstSegmentIsShefCarousel && secondSegmentIsDishCollection && !hasMultipleMealTypes) {
    return [
      content[0],
      <DividerContainer>
        <WavyEdge position={WavyEdge.Position.TOP} isStroke waveSize={WavyEdge.Size.SHORT} />
      </DividerContainer>,
      content[1],
    ];
  }
  return content;
};

const DishFirstExploreSegment = ({
  segment,
  filters,
  updateQueryParams,
  scrollPosition,
  rowNumber,
}: DishFirstExploreSegmentProps) => {
  const { isSpecialtySegment, layout } = getSegmentInfo(filters, segment);

  const segmentMetrics: ISegmentMetrics = useMemo(
    () => ({
      esid: segment.id,
      pos: rowNumber,
      totalItems: segment.content.total,
      renderedItems: segment.content.items.length,
    }),
    [segment.id, rowNumber, segment.content.total, segment.content.items.length]
  );

  const lineItemAvailabilities = useSelector((state) => selectLineItemAvailabilitiesById(state.cart));
  const key = segment.id;
  const baseProps = { lineItemAvailabilities, segment, segmentMetrics, scrollPosition };
  const row = match({
    isSpecialtySegment,
    segmentId: segment.id,
    contentType: getSegmentContentType(segment),
    layout,
  })
    .with({ segmentId: 'CUISINES' }, () => null)
    .with(
      { isSpecialtySegment: true, contentType: 'ExploreSegmentShef', layout: ExploreFoodItemsSegmentLayout.Carousel },
      () => ({
        content: (
          <DishFirstShefsCardRow
            {...baseProps}
            updateQueryParams={updateQueryParams}
            showSeeAllButton={false}
            showHeaderAndSubtitle={false}
          />
        ),
        intercomId: 'shef-segment',
      })
    )
    .with({ isSpecialtySegment: true, contentType: 'ExploreSegmentShef' }, () => ({
      content: <DishFirstShefsCardCollection {...baseProps} showHeader={false} />,
      intercomId: 'shef-segment',
    }))
    .with({ contentType: 'ExploreSegmentShef' }, () => ({
      content: <DishFirstShefsCardRow {...baseProps} updateQueryParams={updateQueryParams} />,
      intercomId: 'shef-segment',
    }))
    .with({ isSpecialtySegment: true }, () => ({
      content: <DishFirstCardCollection key={key} {...baseProps} showHeader={false} />,
      intercomId: 'dish-segment',
    }))
    .with({ segmentId: 'ALL' }, () => ({
      content: <DishFirstCardCollection key={key} {...baseProps} />,
      intercomId: 'dish-segment',
    }))
    .otherwise(() => ({
      content: (
        <DishFirstCardRow
          key={key}
          {...baseProps}
          updateQueryParams={updateQueryParams}
          location={'dish-first-explore-page'}
        />
      ),
      intercomId: 'dish-segment',
    }));

  return row ? <SegmentMargins data-intercom-id={row.intercomId}>{row.content}</SegmentMargins> : null;
};

type IGetSegmentsProps = Pick<PageSegmentsProps, 'data' | 'filters' | 'updateQueryParams' | 'additionalContent'>;
export function getSegments(props: IGetSegmentsProps) {
  const { data, filters, additionalContent, updateQueryParams } = props;
  const content = data?.exploreFoodItems ?? [];
  const contentWithMaybeDividers = insertDividersIfNecessary(filters, content);
  const finalContent = consolidateContentIfNecessary(filters, contentWithMaybeDividers, updateQueryParams);

  const finalAdditionalContent = additionalContent ?? [];
  const segments = reduce<
    (typeof finalAdditionalContent)[number],
    Array<React.ReactNode | DishFirstFoodItemsSegmentFragment>
  >(
    finalAdditionalContent,
    (acc, { node, index }) => [...acc.slice(0, index), node, ...acc.slice(index)],
    finalContent
  );
  return segments;
}

export const PageSegmentsComponent: React.FC<PageSegmentsProps> = ({
  data,
  contentLoading,
  filters,
  updateQueryParams,
  scrollPosition,
  additionalContent = [],
}) => {
  const hasContentfulResults = some(data?.exploreFoodItems, { layout: 'GENERIC' });

  if (!hasContentfulResults && contentLoading) return null;
  if (!hasContentfulResults && !contentLoading) return <NoResults clearQueryParams={filters.clearQueryParams} />;

  const segments = getSegments({ data, filters, updateQueryParams, additionalContent });

  return (
    <div data-intercom-id='explore-segments'>
      {segments.map((element, rowNumber) =>
        match(element)
          .with(P.when(typeGuard), (segment) => (
            <DishFirstExploreSegment
              key={rowNumber}
              segment={segment}
              filters={filters}
              updateQueryParams={updateQueryParams}
              scrollPosition={scrollPosition}
              rowNumber={rowNumber}
            />
          ))
          .otherwise((node) => <React.Fragment key={rowNumber}>{node}</React.Fragment>)
      )}
    </div>
  );
};

const typeGuard = (
  element: DishFirstFoodItemsSegmentFragment | React.ReactNode
): element is DishFirstFoodItemsSegmentFragment =>
  (element as DishFirstFoodItemsSegmentFragment).__typename === 'ExploreFoodItemsSegment';
